import { useEffect, useMemo, useState } from 'react';
import { fetchQuery, useRelayEnvironment } from 'react-relay';

import {
	ContentCollaboratorsQuery,
	type ContentCollaboratorsQueryData,
	type ContentCollaboratorsQueryVariables,
} from '@confluence/details-panel/entry-points/ContentCollaboratorsQuery.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	normalizePageAgentContributors,
	type PageAgentContributor,
} from '../utils/normalizePageAgentContributors.ts';

export type UsePageAgentContributorsResult = {
	agents: PageAgentContributor[];
	loading: boolean;
	error: Error | undefined;
	availability: 'disabled' | 'loading' | 'available' | 'unavailable' | 'error';
};

/** Panel-owned, cache-first read. The modal shares this slice without mounting another hook. */
export const usePageAgentContributors = (
	contentId: string | undefined,
	{ enabled = true }: { enabled?: boolean } = {},
): UsePageAgentContributorsResult => {
	const { cloudId } = useSessionData();
	const environment = useRelayEnvironment();
	const skip = !enabled || !contentId || !cloudId;
	const request = useMemo(
		() => ({ cloudId, contentId, skip, environment }),
		[cloudId, contentId, skip, environment],
	);
	const [result, setResult] = useState<{
		request: typeof request;
		data?: ContentCollaboratorsQueryData;
		error?: Error;
	}>();

	useEffect(() => {
		if (request.skip || !request.contentId || !request.cloudId) {
			return;
		}

		const subscription = fetchQuery<{
			response: ContentCollaboratorsQueryData;
			variables: ContentCollaboratorsQueryVariables;
		}>(
			request.environment,
			ContentCollaboratorsQuery,
			{ cloudId: request.cloudId, id: request.contentId, first: 2147483647 },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (data) => {
				setResult({ request, data });
			},
			error: (error: Error) => {
				setResult({ request, error });
			},
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [request]);

	// Hide previous-page data immediately, before the next subscription starts.
	const currentResult = !skip && result?.request === request ? result : undefined;
	const data = currentResult?.data;
	const resultError = currentResult?.error;
	const loading = !skip && !currentResult;

	useEffect(() => {
		if (resultError && !isErrorMarkedAsHandled(resultError)) {
			// Permission failures leave the independent people/viewer streams available.
			if (!isUnauthorizedError(resultError)) {
				getMonitoringClient().submitError(resultError, { attribution: Attribution.INTEGRATIONS });
			}

			markErrorAsHandled(resultError);
		}
	}, [resultError]);

	const availability = skip
		? 'disabled'
		: loading
			? 'loading'
			: resultError
				? 'error'
				: data?.confluence_contentCollaborators == null
					? 'unavailable'
					: 'available';

	const agents = useMemo(
		() => (availability === 'available' ? normalizePageAgentContributors(data) : []),
		[availability, data],
	);

	return useMemo(
		() => ({ agents, loading: availability === 'loading', error: resultError, availability }),
		[agents, availability, resultError],
	);
};
